// src/components/analytics/TeamProgress.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Users, Target, Award, Zap } from 'lucide-react';

interface TeamProgressProps {
  totalUsers: number;
  activeToday: number;
  weeklyGoal: number;
  weeklyProgress: number;
  totalTestsThisWeek: number;
  averageStreak: number;
}

export const TeamProgress: React.FC<TeamProgressProps> = ({
  totalUsers,
  activeToday,
  weeklyGoal,
  weeklyProgress,
  totalTestsThisWeek,
  averageStreak,
}) => {
  const participationRate = (activeToday / totalUsers) * 100;
  const goalProgress = (weeklyProgress / weeklyGoal) * 100;

  const stats = [
    {
      label: 'Team Members',
      value: totalUsers,
      icon: Users,
      color: 'text-blue-500',
    },
    {
      label: 'Active Today',
      value: activeToday,
      icon: Zap,
      color: 'text-yellow-500',
    },
    {
      label: 'This Week',
      value: totalTestsThisWeek,
      icon: Target,
      color: 'text-green-500',
    },
    {
      label: 'Avg Streak',
      value: Math.round(averageStreak),
      icon: Award,
      color: 'text-purple-500',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className="flex flex-col items-center p-4 rounded-lg bg-muted/50"
            >
              <stat.icon className={`w-8 h-8 mb-2 ${stat.color}`} />
              <div className="text-3xl font-bold">{stat.value}</div>
              <div className="text-xs text-muted-foreground text-center">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Today's Participation</span>
              <span className="text-sm text-muted-foreground">
                {activeToday} / {totalUsers} ({participationRate.toFixed(0)}%)
              </span>
            </div>
            <Progress value={participationRate} className="h-3" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Weekly Goal Progress</span>
              <span className="text-sm text-muted-foreground">
                {weeklyProgress} / {weeklyGoal} ({goalProgress.toFixed(0)}%)
              </span>
            </div>
            <Progress value={goalProgress} className="h-3" />
          </div>
        </div>

        {goalProgress >= 100 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="p-4 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 text-center"
          >
            <div className="text-4xl mb-2">🎉</div>
            <p className="font-bold text-green-500">Weekly Goal Achieved!</p>
            <p className="text-sm text-muted-foreground">Amazing teamwork!</p>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

